import React from 'react';

import { clsx, Group, Loader, MediaQuery, Paper, Stack, Text, TextInput, Title } from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';
import { IconSearch } from '@tabler/icons-react';
import GoBackButton from 'components/BackButton/GoBackButton';
import EventTypeMultiSelectAutoComplete from 'components/form-fields/auto-complete-fields/EventTypeMultiSelectAutoComplete';
import DateRangePicker, { Period } from 'components/form-fields/range-pickers/DateRangePicker';
import HelpButton from 'components/HelpButton';
import useTotalUncreditedReservations from 'hooks/reports/reservations/uncredited-reservations/useTotalUncreditedReservations';
import { useUncreditedReservationsContext } from 'pages/private/owner/reports/UncreditedReservations/UncreditedReservationsContext';
import { EventType } from 'types';

import ClearFilters from './ClearFilters';
import DownloadUncreditedReservationsCSV from './DownloadCSV';
import useStyles from './useStyles';

const UncreditedReservationsPageHeader = (): JSX.Element => {
  const { classes, theme } = useStyles();
  const isDesktop = useMediaQuery(`(min-width: ${theme.breakpoints.sm}`, false);
  const {
    filters,
    setDateRange,
    setEventTypes,
    setSearch,
    clearFilters,
    hasActiveFilters,
    computedFilters,
  } = useUncreditedReservationsContext();

  const {
    data: totalUncreditedReservations,
    isError: isErrorTotalUncreditedReservations,
    isLoading: isLoadingTotalUncreditedReservations,
  } = useTotalUncreditedReservations({
    startDate: computedFilters.startDate,
    endDate: computedFilters.endDate,
  });

  const handleEventTypesChange = (values: EventType[]): void => {
    setEventTypes(values);
  };

  const handleDateRangeChange = (value: Period): void => {
    setDateRange(value);
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    setSearch(event.currentTarget.value);
  };

  const getTotalUncreditedReservations = (): JSX.Element => {
    if (isLoadingTotalUncreditedReservations) {
      return <Loader size='sm' />;
    }

    if (isErrorTotalUncreditedReservations) {
      return <Text c='red'>Error</Text>;
    }

    return (
      <Text className={classes.total} color='dimmed' weight={'bold'}>
        {totalUncreditedReservations?.data.total}
      </Text>
    );
  };

  // Extract filters to avoid duplication
  const filtersGroup = (
    <Group align='center' position='apart' spacing='sm' noWrap w='auto'>
      <TextInput
        className={classes.searchFilter}
        icon={<IconSearch size={16} />}
        value={filters.search}
        onChange={handleSearchChange}
        placeholder='Search athlete...'
        label=''
      />
      <DateRangePicker
        className={classes.dateRangeFilter}
        presets={['this_month', 'next_7_days', 'rest_of_month']}
        placeholder='Date Range'
        value={filters.dateRange}
        onChange={handleDateRangeChange}
      />
      <EventTypeMultiSelectAutoComplete
        placeholder='Event Type'
        handleChange={handleEventTypesChange}
        values={filters.eventTypes}
        className={classes.eventTypesFilter}
      />
    </Group>
  );

  const titleGroup = (
    <Group noWrap align='center'>
      <GoBackButton />
      <Title className={classes.title}>Uncredited Reservations</Title>
      {getTotalUncreditedReservations()}
      <HelpButton width={400} position={isDesktop ? 'right' : 'bottom'}>
        <Text size='sm' sx={{ textWrap: 'auto' }}>
          This report highlights athletes with uncredited reservations. It shows the next 7 days by default,
          with each row starting from their soonest upcoming reservation. Use this report to apply credits or
          remove upcoming reservations.
        </Text>
      </HelpButton>
    </Group>
  );

  const optionsGroup = (
    <Group spacing='sm' noWrap>
      <ClearFilters onClick={clearFilters} disabled={!hasActiveFilters} />
      <DownloadUncreditedReservationsCSV className={classes.download} />
    </Group>
  );

  return (
    <Paper className={clsx(classes.baseHeader)}>
      {/* Desktop layout */}
      <MediaQuery smallerThan='lg' styles={{ display: 'none' }}>
        <Group noWrap spacing='sm' position='apart'>
          {titleGroup}
          <Group spacing='md' noWrap>
            {filtersGroup}
            {optionsGroup}
          </Group>
        </Group>
      </MediaQuery>

      {/* Mobile layout */}
      <MediaQuery largerThan='lg' styles={{ display: 'none' }}>
        <Stack spacing='sm'>
          <Group position='apart' noWrap>
            {titleGroup}
            {optionsGroup}
          </Group>
          {filtersGroup}
        </Stack>
      </MediaQuery>
    </Paper>
  );
};

export default UncreditedReservationsPageHeader;
